/**
 * Reporter-facing reports page. Shows the caller's own reports as a grid
 * of themed cards, plus a leaderboard sub-view and a nickname editor for
 * public display preferences.
 *
 * Visual grammar matches the rest of the app:
 *   - Gold accents (#c9a227) on borders and headings
 *   - Dark cards (#1e1624 / #2a2137 backgrounds)
 *   - Status indicated by left border color: red (open), purple
 *     (addressed), green (resolved)
 *   - Importance shown as ★ / 10
 *
 * Cards are CSS-grid `repeat(auto-fill, minmax(280px, 1fr))`. Long titles
 * truncated at the first ~10 words. Click a card → ReportThread.
 */

import React from 'react';
import {
  useMyBugReports,
  useBugReportLeaderboard,
  useMyPreferences,
  useSetNickname,
  useReporterMarkResolved,
} from '../hooks/useBugReports.js';
import ReportThread from './ReportThread.jsx';
import {
  TAG_COLORS,
  STATUS_FILL,
  STATUS_BORDER,
  STATUS_LABEL,
  CATEGORY_ORDER,
  primaryCategory,
  normalizeTag,
} from '../tagPalette.js';

const safeParse = (s) => {
  if (typeof s !== 'string' || !s) return {};
  try { return JSON.parse(s) || {}; } catch { return {}; }
};

const truncateTitle = (title, words = 10) => {
  if (!title) return '(untitled)';
  const arr = title.split(/\s+/);
  if (arr.length <= words) return title;
  return arr.slice(0, words).join(' ') + '…';
};

// --- ReportCard --------------------------------------------------------

// Render the importance label. Critical reports show no badge — the
// "critical" tag pill carries the message. Otherwise prefer raw/scale
// (when stored) over legacy importance (1-10 default).
const renderImportance = (parsed) => {
  if (Array.isArray(parsed.tags) && parsed.tags.includes('critical')) return null;
  if (typeof parsed.importanceRaw === 'number' && typeof parsed.importanceScale === 'number') {
    return `★ ${parsed.importanceRaw}/${parsed.importanceScale}`;
  }
  if (typeof parsed.importance === 'number') {
    return `★ ${parsed.importance}/10`;
  }
  return null;
};

function ReportCard({ report, onOpen, onMarkResolved }) {
  const parsed = safeParse(report.data);
  const tags = (Array.isArray(parsed.tags) ? parsed.tags : []).map(normalizeTag);
  const unread = report.hasUnreadForReporter;
  // Left border = category color (so the eye scans by category at a
  // glance). Status is communicated by the fill tint + status pill.
  const cat = primaryCategory(parsed.tags);
  const categoryColor = TAG_COLORS[cat] || '#888';
  const statusBorder = STATUS_BORDER[report.status] || '#888';
  const statusFill = STATUS_FILL[report.status] || 'transparent';
  const statusLabel = STATUS_LABEL[report.status] || report.status;
  const importanceLabel = renderImportance(parsed);
  const isAddressed = report.status === 'addressed';
  const [busyResolve, setBusyResolve] = React.useState(false);

  // Quick-resolve handler — confirms the dev's fix without opening the
  // thread. Stops propagation so the card click (which opens the thread)
  // doesn't fire alongside.
  const handleQuickResolve = async (e) => {
    e.stopPropagation();
    if (busyResolve) return;
    setBusyResolve(true);
    try {
      await onMarkResolved({ reportId: report.reportId });
    } catch (err) {
      console.error('Quick resolve failed:', err);
      alert(err?.message || 'Mark resolved failed.');
    } finally {
      setBusyResolve(false);
    }
  };

  // Background gradient gets a tint from STATUS_FILL on top of the card
  // base. Addressed = blue-green leaning green (waiting on you).
  // Resolved = deep saturated green (done done). Active thread on open
  // = light blue (a conversation in progress, distinct from the green
  // family so it can't be confused with addressed/resolved).
  const isActiveThread = report.status === 'open' && (report.commentCount || 0) > 0;
  const effectiveFill = isActiveThread ? STATUS_FILL.threadOnOpen : statusFill;
  const cardBg = (report.status === 'open' && !isActiveThread)
    ? 'linear-gradient(180deg, #1e1624 0%, #2a2137 100%)'
    : `linear-gradient(180deg, ${effectiveFill} 0%, ${effectiveFill} 100%), linear-gradient(180deg, #1e1624 0%, #2a2137 100%)`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(report.reportId)}
      onKeyDown={e => { if (e.key === 'Enter') onOpen(report.reportId); }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left',
        gap: 6,
        padding: 14,
        background: cardBg,
        borderRadius: 8,
        border: '1px solid rgba(201,162,39,0.2)',
        borderLeft: `4px solid ${categoryColor}`,
        color: '#e8d5b7',
        cursor: 'pointer',
        boxShadow: unread ? `0 0 0 1px ${statusBorder}77, 0 4px 12px rgba(0,0,0,0.4)` : '0 2px 8px rgba(0,0,0,0.3)',
        transition: 'transform 120ms ease-out, box-shadow 120ms ease-out',
        minHeight: 130,
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{
          padding: '2px 8px',
          borderRadius: 8,
          background: `${statusBorder}22`,
          color: statusBorder,
          border: `1px solid ${statusBorder}55`,
          fontSize: '0.62rem',
          fontWeight: 600,
        }}>{statusLabel}</span>
        {unread && (
          <span
            title="Unread update"
            style={{
              width: 8, height: 8, borderRadius: '50%',
              background: statusBorder,
              boxShadow: `0 0 6px ${statusBorder}`,
            }}
          />
        )}
        {importanceLabel && (
          <span style={{ marginLeft: 'auto', fontSize: '0.65rem', color: '#c9a227', fontWeight: 600 }}>
            {importanceLabel}
          </span>
        )}
      </div>

      <div style={{
        fontSize: '0.92rem',
        fontWeight: 600,
        color: '#e8d5b7',
        lineHeight: 1.3,
      }}>
        {truncateTitle(parsed.title, 10)}
      </div>

      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {tags.slice(0, 4).map((t, i) => (
            <span key={`${t}-${i}`} style={{
              padding: '1px 6px',
              borderRadius: 6,
              background: `${TAG_COLORS[t] || '#888'}1a`,
              color: TAG_COLORS[t] || '#888',
              fontSize: '0.58rem',
            }}>{t}</span>
          ))}
        </div>
      )}

      <div style={{ marginTop: 'auto', fontSize: '0.6rem', color: '#666' }}>
        Updated {new Date(report.lastActivityAt).toLocaleString()}
      </div>

      {/* Inline quick-resolve for addressed reports — the dev marked it
          fixed; the reporter just needs to confirm. Visible on the card
          so they don't have to open the thread to confirm.  */}
      {isAddressed && (
        <button
          type="button"
          onClick={handleQuickResolve}
          disabled={busyResolve}
          style={{
            marginTop: 4,
            padding: '6px 10px',
            fontSize: '0.7rem',
            fontWeight: 700,
            background: 'linear-gradient(180deg, rgba(39,201,90,0.25), rgba(39,201,90,0.1))',
            border: '1px solid #27c95a',
            borderRadius: 5,
            color: '#27c95a',
            cursor: busyResolve ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          ✓ Mark as resolved
        </button>
      )}

      {/* "Start thread" CTA — visible on open reports without comments
          yet. Hint that the reporter can add a comment to clarify or
          extend the report. Clicking it opens the thread (where the
          composer sits at the bottom). */}
      {report.status === 'open' && (report.commentCount || 0) === 0 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpen(report.reportId); }}
          style={{
            marginTop: 4,
            padding: '6px 10px',
            fontSize: '0.7rem',
            fontWeight: 600,
            background: 'rgba(74,144,217,0.10)',
            border: '1px solid rgba(74,144,217,0.45)',
            borderRadius: 5,
            color: '#7eb6e8',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          💬 Start a thread
        </button>
      )}
    </div>
  );
}

// --- NicknameEditor ----------------------------------------------------

function NicknameEditor() {
  const prefs = useMyPreferences();
  const setNickname = useSetNickname();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (prefs?.nickname && !editing) setDraft(prefs.nickname);
  }, [prefs, editing]);

  const onSave = async () => {
    setBusy(true);
    try {
      await setNickname({ nickname: draft.trim() });
      setEditing(false);
    } catch (e) { console.error(e); alert(e?.message || 'Save failed.'); }
    finally { setBusy(false); }
  };

  if (prefs === undefined) return null;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 12px',
      background: 'rgba(201,162,39,0.06)',
      border: '1px solid rgba(201,162,39,0.18)',
      borderRadius: 6,
      fontSize: '0.78rem',
      flexWrap: 'wrap',
    }}>
      <span style={{ color: '#888' }}>Public name:</span>
      {!editing ? (
        <>
          <span style={{ color: '#c9a227', fontWeight: 600 }}>
            {prefs?.nickname || <em style={{ color: '#888', fontWeight: 400 }}>your first name</em>}
          </span>
          <button
            onClick={() => { setDraft(prefs?.nickname || ''); setEditing(true); }}
            style={{
              padding: '2px 8px',
              fontSize: '0.7rem',
              background: 'transparent',
              border: '1px solid rgba(201,162,39,0.3)',
              borderRadius: 4,
              color: '#c9a227',
              cursor: 'pointer',
            }}
          >
            {prefs?.nickname ? 'Edit' : 'Set nickname'}
          </button>
          <span style={{ color: '#666', fontSize: '0.65rem' }}>
            (last name is hidden by default)
          </span>
        </>
      ) : (
        <>
          <input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Pick a nickname (or leave empty for first name)"
            maxLength={40}
            style={{
              flex: '1 1 200px',
              padding: '4px 8px',
              fontSize: '0.78rem',
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(201,162,39,0.3)',
              borderRadius: 4,
              color: '#e8d5b7',
              outline: 'none',
            }}
          />
          <button
            onClick={onSave}
            disabled={busy}
            style={{
              padding: '4px 10px', fontSize: '0.7rem',
              background: 'rgba(39,201,90,0.15)', border: '1px solid #27c95a',
              borderRadius: 4, color: '#27c95a', cursor: busy ? 'wait' : 'pointer',
            }}
          >
            Save
          </button>
          <button
            onClick={() => setEditing(false)}
            style={{
              padding: '4px 10px', fontSize: '0.7rem',
              background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 4, color: '#888', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </>
      )}
    </div>
  );
}

// --- LeaderboardView ---------------------------------------------------

function LeaderboardView({ onBack }) {
  const rows = useBugReportLeaderboard(50);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button
          onClick={onBack}
          style={{
            padding: '6px 12px',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 5,
            color: '#888',
            cursor: 'pointer',
            fontSize: '0.78rem',
          }}
        >← Back</button>
        <h2 style={{ ...PAGE_TITLE_STYLE, fontSize: '1.4rem' }}>🏆 Reporter Leaderboard</h2>
      </div>
      {rows === undefined ? (
        <div style={{ color: '#888', padding: 20 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: '#666', padding: 20 }}>No reports yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map((r, i) => (
            <div
              key={r.userId || r.displayName || `reporter-${i}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                background: i < 3 ? 'rgba(201,162,39,0.08)' : 'rgba(255,255,255,0.02)',
                border: `1px solid ${i < 3 ? 'rgba(201,162,39,0.25)' : 'rgba(255,255,255,0.06)'}`,
                borderRadius: 6,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  color: i === 0 ? '#ffd700' : i === 1 ? '#c0c0c0' : i === 2 ? '#cd7f32' : '#c9a227',
                  fontWeight: 700,
                  minWidth: 32,
                  fontSize: '0.95rem',
                }}>#{i + 1}</span>
                <span style={{ color: '#e8d5b7', fontWeight: 500 }}>{r.displayName || '(anonymous)'}</span>
              </div>
              <span style={{ color: '#c9a227', fontWeight: 700, fontSize: '0.95rem' }}>
                {r.count} {r.count === 1 ? 'report' : 'reports'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Page --------------------------------------------------------------

// A report counts as a "thread" once it has actual back-and-forth
// (commentCount > 0). Questions and feedback are inherently
// conversational, so they show as threads even without a comment yet —
// reporter and dev are expected to talk through them.
const isThread = (report) => {
  if ((report.commentCount || 0) > 0) return true;
  let parsedTags = [];
  try { parsedTags = JSON.parse(report.data || '{}').tags || []; } catch { /* ignore */ }
  return parsedTags.some(t => t === 'question' || t === 'feedback');
};

// Filter tabs include a dedicated "For review" view that surfaces the
// reports the dev marked addressed but the reporter hasn't yet
// confirmed. Visually distinct (light green) so the reporter knows
// what's waiting on them. "Threads" surfaces actual conversations
// (commentCount > 0) plus questions/feedback (inherently conversational).
const STATUS_FILTERS = [
  { id: 'review',    label: '✨ For review', match: r => r.status === 'addressed', accent: '#78dc8c' },
  { id: 'open',      label: 'Open',         match: r => r.status === 'open' },
  { id: 'threads',   label: '💬 Threads',   match: r => isThread(r) && r.status !== 'resolved', accent: '#4a90d9' },
  { id: 'resolved',  label: 'Resolved',     match: r => r.status === 'resolved' },
  { id: 'all',       label: 'All',          match: () => true },
];

const effectiveImportance = (parsed) => {
  if (Array.isArray(parsed.tags) && parsed.tags.includes('critical')) return 11;
  return typeof parsed.importance === 'number' ? parsed.importance : 0;
};

// Status-based subsections for the reporter's "All" view. For Review
// goes first, even when empty (the slot stays so the reporter learns
// where confirmation requests will land). Each subsection has its own
// label, accent, and matcher.
const ALL_VIEW_SECTIONS = [
  { id: 'review',    label: '✨ For review',  accent: '#78dc8c',
    description: 'The dev fixed these. Test and confirm.',
    match: r => r.status === 'addressed' },
  { id: 'threads',   label: '💬 Active threads', accent: '#4a90d9',
    description: 'Open conversations — questions, feedback, or comment threads.',
    match: r => r.status === 'open' && (typeof isThread === 'function' ? isThread(r) : false) },
  { id: 'open',      label: '📂 Open',         accent: '#ff6961',
    description: "Reports awaiting the dev's attention.",
    match: r => r.status === 'open' && (typeof isThread === 'function' ? !isThread(r) : true) },
  { id: 'resolved',  label: '✅ Resolved',     accent: '#27c95a',
    description: 'Confirmed fixed.',
    match: r => r.status === 'resolved' },
];

export default function MyReportsPage({ onBack }) {
  const [openReportId, setOpenReportId] = React.useState(null);
  const [showLeaderboard, setShowLeaderboard] = React.useState(false);
  const [search, setSearch] = React.useState('');
  // Default to "all" so For Review sits at the top of the page, with
  // Open / Threads / Resolved beneath it. The reporter sees what's
  // waiting on them at a glance — and can use the filter tabs to drill
  // into a single subsection.
  const [statusFilter, setStatusFilter] = React.useState('all');
  const reporterMarkResolved = useReporterMarkResolved();

  const mine = useMyBugReports(100);
  const items = React.useMemo(() => mine.results || [], [mine.results]);
  const loading = mine.status === 'LoadingFirstPage';
  const canLoadMore = mine.status === 'CanLoadMore';

  // "All" view groups by status (For Review → Open Threads → Open →
  // Resolved). Within each group, sort by importance desc so critical
  // bubbles up. Category coloring lives on the card border, so the
  // reporter can scan by both axes simultaneously.
  const groupedForAll = React.useMemo(() => {
    if (statusFilter !== 'all') return null;
    const q = search.trim().toLowerCase();
    const filtered = items.filter(r => {
      if (!q) return true;
      const p = safeParse(r.data);
      return (p.title || '').toLowerCase().includes(q)
        || (p.description || '').toLowerCase().includes(q);
    });
    const buckets = new Map();
    for (const sec of ALL_VIEW_SECTIONS) buckets.set(sec.id, []);
    for (const r of filtered) {
      const p = safeParse(r.data);
      // First matching section wins. Sections are ordered so review →
      // threads → open → resolved.
      const sec = ALL_VIEW_SECTIONS.find(s => s.match(r));
      if (sec) buckets.get(sec.id).push({ report: r, parsed: p });
    }
    for (const arr of buckets.values()) {
      arr.sort((a, b) => {
        const ai = effectiveImportance(a.parsed);
        const bi = effectiveImportance(b.parsed);
        if (ai !== bi) return bi - ai;
        return b.report.lastActivityAt - a.report.lastActivityAt;
      });
    }
    return buckets;
  }, [items, search, statusFilter]);

  const filtered = React.useMemo(() => {
    if (statusFilter === 'all') return [];
    const q = search.trim().toLowerCase();
    const matchFn = STATUS_FILTERS.find(f => f.id === statusFilter)?.match;
    let out = items.slice();
    if (q) {
      out = out.filter(r => {
        const p = safeParse(r.data);
        return (p.title || '').toLowerCase().includes(q)
          || (p.description || '').toLowerCase().includes(q);
      });
    }
    if (matchFn) out = out.filter(matchFn);
    out.sort((a, b) => {
      // Unread for reporter goes first within filter bucket.
      if (a.hasUnreadForReporter !== b.hasUnreadForReporter) {
        return a.hasUnreadForReporter ? -1 : 1;
      }
      // Then by importance descending.
      const ai = effectiveImportance(safeParse(a.data));
      const bi = effectiveImportance(safeParse(b.data));
      if (ai !== bi) return bi - ai;
      return b.lastActivityAt - a.lastActivityAt;
    });
    return out;
  }, [items, search, statusFilter]);

  if (openReportId) {
    return (
      <div style={pageWrapStyle}>
        <div style={pageInnerStyle}>
          <ReportThread
            reportId={openReportId}
            onBack={() => setOpenReportId(null)}
            onOpenReport={(id) => setOpenReportId(id)}
          />
        </div>
      </div>
    );
  }

  if (showLeaderboard) {
    return (
      <div style={pageWrapStyle}>
        <div style={pageInnerStyle}>
          <LeaderboardView onBack={() => setShowLeaderboard(false)} />
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrapStyle}>
      <div style={pageInnerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          {onBack && (
            <button
              onClick={onBack}
              style={{
                padding: '6px 12px',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 5,
                color: '#888',
                cursor: 'pointer',
                fontSize: '0.78rem',
              }}
            >← Back</button>
          )}
          <h1 style={PAGE_TITLE_STYLE}>🪲 My Reports</h1>
          <span style={{ marginLeft: 'auto' }}>
            <button
              onClick={() => setShowLeaderboard(true)}
              style={{
                padding: '6px 14px',
                background: 'rgba(201,162,39,0.12)',
                border: '1px solid rgba(201,162,39,0.4)',
                borderRadius: 5,
                color: '#c9a227',
                cursor: 'pointer',
                fontSize: '0.78rem',
              }}
            >🏆 Leaderboard</button>
          </span>
        </div>

        <div style={{ marginBottom: 14 }}>
          <NicknameEditor />
        </div>

        <div style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
          padding: '10px 14px',
          background: 'linear-gradient(180deg, rgba(201,162,39,0.04), rgba(0,0,0,0.25))',
          border: '1px solid rgba(201,162,39,0.18)',
          borderRadius: 8,
          marginBottom: 14,
        }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search title or description…"
            style={{
              flex: '1 1 200px',
              padding: '5px 10px',
              fontSize: '0.8rem',
              background: 'rgba(0,0,0,0.4)',
              border: '1px solid rgba(201,162,39,0.25)',
              borderRadius: 5,
              color: '#e8d5b7',
              outline: 'none',
            }}
          />
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {STATUS_FILTERS.map(s => {
              const active = statusFilter === s.id;
              const accent = s.accent || '#c9a227';
              const count = s.id === 'all'
                ? items.length
                : items.filter(s.match).length;
              return (
                <button
                  key={s.id}
                  onClick={() => setStatusFilter(s.id)}
                  style={{
                    padding: '4px 10px',
                    fontSize: '0.7rem',
                    background: active ? `${accent}22` : 'transparent',
                    border: `1px solid ${active ? accent : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: 4,
                    color: active ? accent : '#888',
                    cursor: 'pointer',
                    fontWeight: active ? 600 : 400,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <span>{s.label}</span>
                  {count > 0 && (
                    <span style={{
                      fontSize: '0.55rem',
                      padding: '0 5px',
                      borderRadius: 8,
                      background: active ? `${accent}33` : 'rgba(255,255,255,0.05)',
                      color: active ? accent : '#888',
                    }}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {loading && <div style={{ color: '#888', padding: 20 }}>Loading reports…</div>}

        {!loading && statusFilter === 'all' && groupedForAll && (
          (() => {
            const totalCount = Array.from(groupedForAll.values()).reduce((acc, arr) => acc + arr.length, 0);
            if (totalCount === 0) {
              return (
                <div style={{
                  padding: 32,
                  textAlign: 'center',
                  color: '#666',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px dashed rgba(201,162,39,0.2)',
                  borderRadius: 8,
                }}>
                  {items.length === 0
                    ? 'No reports yet. Click the 🪲 button (bottom-right) to file the first one!'
                    : 'No reports match the current search.'}
                </div>
              );
            }
            return ALL_VIEW_SECTIONS.map(sec => {
              const arr = groupedForAll.get(sec.id) || [];
              // For Review section gets a placeholder card even when
              // empty so the reporter learns where confirmation requests
              // will appear. Other sections collapse when empty.
              if (arr.length === 0 && sec.id !== 'review') return null;
              return (
                <div key={sec.id} style={{ marginBottom: 18 }}>
                  <div style={{ ...SECTION_DIVIDER_STYLE, margin: '12px 0 6px' }}>
                    <span style={{ ...SECTION_HEADING_STYLE, color: sec.accent }}>
                      {sec.label} <span style={{ color: '#666', fontWeight: 400 }}>({arr.length})</span>
                    </span>
                    <span style={{ ...SECTION_RULE_STYLE, background: `linear-gradient(90deg, ${sec.accent}66, transparent)` }} />
                  </div>
                  <div style={{ fontSize: '0.65rem', color: '#888', marginBottom: 10, fontStyle: 'italic' }}>
                    {sec.description}
                  </div>
                  {arr.length === 0 ? (
                    <div style={{
                      padding: 16, textAlign: 'center', color: '#666', fontSize: '0.75rem',
                      background: 'rgba(120,220,140,0.04)',
                      border: '1px dashed rgba(120,220,140,0.2)', borderRadius: 6,
                    }}>
                      Nothing waiting on you here. When the dev marks a report
                      addressed, it'll show up here for confirmation.
                    </div>
                  ) : (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: 12,
                    }}>
                      {arr.map(({ report }) => (
                        <ReportCard
                          key={report.reportId}
                          report={report}
                          onOpen={setOpenReportId}
                          onMarkResolved={reporterMarkResolved}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            });
          })()
        )}

        {!loading && statusFilter !== 'all' && filtered.length === 0 && (
          <div style={{
            padding: 32,
            textAlign: 'center',
            color: '#666',
            background: 'rgba(255,255,255,0.02)',
            border: '1px dashed rgba(201,162,39,0.2)',
            borderRadius: 8,
          }}>
            {items.length === 0
              ? 'No reports yet. Click the 🪲 button (bottom-right) to file the first one!'
              : 'No reports match the current filters.'}
          </div>
        )}

        {!loading && statusFilter !== 'all' && filtered.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 12,
          }}>
            {filtered.map(r => (
              <ReportCard
                key={r.reportId}
                report={r}
                onOpen={setOpenReportId}
                onMarkResolved={reporterMarkResolved}
              />
            ))}
          </div>
        )}

        {canLoadMore && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <button
              onClick={() => mine.loadMore(50)}
              style={{
                padding: '8px 20px',
                background: 'rgba(74,144,217,0.12)',
                border: '1px solid rgba(74,144,217,0.4)',
                borderRadius: 6,
                color: '#4a90d9',
                cursor: 'pointer',
                fontSize: '0.78rem',
              }}
            >
              Load 50 more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Shared page styling — mirrors the app's fantasy aesthetic:
//   - Vertical gradient (deep purple-black → warm dark)
//   - Subtle gold radial glow at the top (matches the "candle-lit
//     parchment" vibe in the rest of the app)
//   - Parchment text color throughout
//   - Serif treatment on headings (matches the AuthGate sign-in screen
//     and the rest of the high-level chrome).
//
// Exported so DevDashboardPage can reuse the exact same surface — the
// two pages should feel like siblings.
export const PAGE_WRAP_STYLE = {
  minHeight: '100vh',
  width: '100%',
  color: '#e8d5b7',
  background: `
    radial-gradient(ellipse 800px 400px at 50% -200px, rgba(201,162,39,0.12), transparent 70%),
    linear-gradient(180deg, #1a1008 0%, #221c2a 50%, #1a0e1a 100%)
  `,
};

export const PAGE_INNER_STYLE = {
  maxWidth: 1200,
  margin: '0 auto',
  padding: '32px 20px 96px',
};

export const PAGE_TITLE_STYLE = {
  margin: 0,
  color: '#c9a227',
  fontSize: '1.7rem',
  fontFamily: 'Georgia, "Times New Roman", serif',
  fontWeight: 700,
  letterSpacing: '0.02em',
  textShadow: '0 2px 12px rgba(201,162,39,0.25)',
};

export const PAGE_CARD_STYLE = {
  background: 'linear-gradient(180deg, rgba(42,33,55,0.6) 0%, rgba(30,22,36,0.6) 100%)',
  border: '1px solid rgba(201,162,39,0.25)',
  borderRadius: 10,
  boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
};

export const SECTION_DIVIDER_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  margin: '24px 0 14px',
};

export const SECTION_HEADING_STYLE = {
  fontSize: '0.95rem',
  color: '#c9a227',
  fontFamily: 'Georgia, "Times New Roman", serif',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

export const SECTION_RULE_STYLE = {
  flex: 1,
  height: 1,
  background: 'linear-gradient(90deg, rgba(201,162,39,0.5), transparent)',
};

const pageWrapStyle = PAGE_WRAP_STYLE;
const pageInnerStyle = PAGE_INNER_STYLE;
